/**
 * Workbench plugin contracts — the composable IDE extension model.
 *
 * A plugin is a declarative DESCRIPTOR (pure data + component references, no
 * lifecycle engine) that contributes UI + data to the workbench through a small
 * set of extension points. Products decide which plugins to mount: Studio opts
 * into integrations (OTLP telemetry, …); the viewer mounts none. See
 * docs/2026-07-23-workbench-plugins.md.
 *
 * This formalizes the pattern the codebase already uses everywhere — an external
 * store behind a React context that degrades to a NOOP when no provider is
 * mounted (liveTrafficStore, hoverStore, NodeColoring, DiffContext) — so a
 * plugin's data feed is just its own provider, and its UI is a contribution the
 * host renders at a known seam.
 */
import type { ComponentType, ReactNode } from "react";
import type { BoardEdge, BoardItem, PlannedRoute } from "@archlang/scene/board";
import type { WorkbenchHostContext, OpenTabSpec } from "./hostContext.js";
import type { ViewModeConfig } from "../editorLayout.js";

/** A JSON-safe value — the shape a custom-tab plugin owns for its opaque tab
 *  state, so the store can persist it verbatim (it round-trips through
 *  `JSON.stringify` with no custom (de)serializer). */
export type JsonValue =
  | null
  | boolean
  | number
  | string
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };

/** A model entity that was removed on the latest recompile — handed to a custom
 *  tab's `onEntityRemoved` so it can decide whether to stay or close. `kind`
 *  matches the model's entity families; `id` is that family's stable id
 *  (module/process key, interface qualified name, type/view name). */
export interface RemovedEntityRef {
  readonly kind: "module" | "process" | "interface" | "type" | "view";
  readonly id: string;
}

/** A layer drawn in board space, inside each space's edge overlay (the seam
 *  where the live-traffic particle layer lives today). It gets the space's edges,
 *  the planned routes (a Map keyed by edge id, viewport-independent), and the
 *  space's nodes (a Map keyed by module id, with position + size in the same
 *  coordinates) — so a layer can draw between nodes that no modeled edge joins
 *  (e.g. an undocumented observed edge). It reads its own data from its plugin's
 *  store and draws in board coordinates. */
export interface BoardLayerContribution {
  readonly id: string;
  readonly Layer: ComponentType<{
    readonly edges: readonly BoardEdge[];
    readonly routes: ReadonlyMap<string, PlannedRoute>;
    readonly nodes: ReadonlyMap<string, BoardItem>;
  }>;
}

/** What the details panel currently has selected. Matches the workbench's
 *  selection truth: an edge is identified by its board edge id plus its endpoint
 *  MODULE ids (`from`/`to`), which is also the join key OTel telemetry uses. */
export type DetailSelection =
  | { readonly kind: "module"; readonly moduleId: string }
  | { readonly kind: "edge"; readonly edgeId: string; readonly from: string; readonly to: string }
  | { readonly kind: "interface"; readonly moduleId: string; readonly interfaceName: string };

/** An extra section rendered in the details panel for a matching selection. The
 *  panel asks each contribution `appliesTo(selection)` and renders those that say
 *  yes, adjacent to the built-in lens section. */
export interface DetailSectionContribution {
  readonly id: string;
  appliesTo(selection: DetailSelection): boolean;
  readonly Section: ComponentType<{ readonly selection: DetailSelection }>;
}

/** A board-chrome header item, merged into the existing `chromeTrailingItems`
 *  slot — not a new command/menu system. */
export interface HeaderItemContribution {
  readonly id: string;
  readonly node: ReactNode;
}

/** An item rendered in the status bar, left- or right-aligned. Gated by
 *  `when(ctx)` against the host's live capabilities — e.g. a poller-health
 *  item hides itself when its plugin has no active poll running. A missing
 *  `when` is always-visible. */
export interface StatusItemContribution {
  readonly id: string;
  readonly align: "left" | "right";
  when?(ctx: WorkbenchHostContext): boolean;
  readonly node: ReactNode;
}

/** A left-rail nav section — an extra icon tab in the WorkspaceNav strip plus
 *  the panel it shows when active. The generalization of the (previously
 *  hardcoded) Files/Types/Processes/Docs tabs into a registry-fed seam. Gated by
 *  `when(ctx)` against the live host ctx (a missing `when` is always-visible);
 *  `order` sorts sections among themselves (built-in mode tabs come first). */
export interface NavSectionContribution {
  readonly id: string;
  /** Accessible name + tooltip for the tab. */
  readonly title: string;
  readonly icon?: ReactNode;
  readonly order?: number;
  when?(ctx: WorkbenchHostContext): boolean;
  /** The panel body rendered in the nav when this section's tab is active. */
  readonly Panel: ComponentType<{ readonly ctx: WorkbenchHostContext }>;
}

/**
 * A custom main-panel tab kind — the contract behind "custom tabs and panels".
 * The store stays the tab-list authority; a plugin owns only its opaque
 * `state: JsonValue` and how to (de)serialize + dedup + tear it down. Opened via
 * `ctx.openTab({ kind: "custom", pluginId, panelKind, state })`; an unknown
 * `pluginId`/`panelKind` on reload is dropped (like an apiVersion mismatch).
 */
export interface PanelKind {
  /** Namespaced kind id, e.g. "history:proposal" — the registry key. */
  readonly kind: string;
  /** Display title for the tab strip, from the open spec. */
  title(spec: OpenTabSpec): string;
  readonly icon?: ReactNode;
  readonly Renderer: ComponentType<{
    readonly state: JsonValue;
    readonly ctx: WorkbenchHostContext;
  }>;
  /** This kind's editor/companion layout, or omitted → view-only (the panel
   *  fills; no board/editor split). Never enters `VIEW_MODE_BY_KIND`. */
  readonly layout?: ViewModeConfig;
  /** Dedup key derived from the open spec (peer of the built-in id derivations);
   *  a repeat `openTab` with the same key activates the existing tab. */
  idFor(spec: OpenTabSpec): string;
  /** Map live state → the JSON persisted in the tab array. */
  serialize(state: JsonValue): JsonValue;
  /** Rebuild live state from persisted JSON on reload; `null` → drop the tab. */
  revive(raw: JsonValue): JsonValue | null;
  /** Decide whether the tab survives a model entity's removal (peer of
   *  `reconcileFileTabs`); omitted → always keep. */
  onEntityRemoved?(state: JsonValue, removed: RemovedEntityRef): "keep" | "close";
}

/** The second step of a two-step command: the palette collects a line (placeholder
 *  = `label`), then calls `commit(value)`. A `CommandContribution.run(ctx)` returns
 *  one of these to opt into the type-a-name flow (the process palette's
 *  "Add step after…"); returning void is an immediate command. */
export interface PromptStep {
  readonly label: string;
  readonly commit: (value: string) => void;
}

/** A command surfaced in the root ⌘K palette. NOT flat: `run(ctx)` may return a
 *  {@link PromptStep} (two-step type-a-name flow), `danger` styles it destructive,
 *  and any mutation `run` performs must go through an undo-journaling action on
 *  `ctx`. `when(ctx)` gates visibility against the host's live capabilities
 *  (re-evaluated at palette open); a missing `when` is always-visible. An optional
 *  `hotkey` is claimed through the hotkey registry (first-registration-wins). */
export interface CommandContribution {
  readonly id: string;
  readonly title: string;
  readonly keywords?: readonly string[];
  readonly group?: string;
  readonly danger?: boolean;
  /** A chord (e.g. "mod+shift+l") claimed via the hotkey registry. */
  readonly hotkey?: string;
  when?(ctx: WorkbenchHostContext): boolean;
  run(ctx: WorkbenchHostContext): void | PromptStep;
}

/** An app-menu item surfaced in the brand/app menu dropdown (Studio's BrandMenu).
 *  The registry peer of the export-share section that used to flow through the
 *  module-global `exportSectionStore`. `group` labels a divider section (items
 *  sharing a group render together under that heading); `order` sorts items;
 *  `when(ctx)` gates visibility (a missing `when` is always-visible). `run(ctx)`
 *  acts against the host — an absent capability makes it a NOOP. Resolved by the
 *  shared `{when,run}` resolver, read OUT of the plugin host (the brand menu
 *  mounts above the editor route) through the registry bridge. */
export interface MenuItemContribution {
  readonly id: string;
  /** Section heading; items with the same group render together. */
  readonly group?: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly order?: number;
  readonly danger?: boolean;
  /** Render greyed + non-interactive (e.g. an export item mid-capture). */
  readonly disabled?: boolean;
  when?(ctx: WorkbenchHostContext): boolean;
  run(ctx: WorkbenchHostContext): void;
}

/** The surfaces a `contextMenu` contribution can extend. Each right-click menu
 *  the workbench builds (board node/edge, nav file, process step, tab) asks the
 *  registry for contributions matching its target and appends them after the
 *  built-in items. */
export type ContextMenuTarget = "node" | "edge" | "file" | "process" | "tab";

/** One menu row a {@link ContextMenuContribution} yields — mapped by the hosting
 *  surface to its native menu item. `run` is already bound (via the closure that
 *  produced it) to the right-clicked target, so it takes no argument. */
export interface MenuItemSpec {
  readonly label: string;
  readonly danger?: boolean;
  readonly separator?: boolean;
  run(): void;
}

/** Extra right-click menu rows for a given target, rendered AFTER the built-in
 *  items. `items(ctx, targetId)` is called when the menu opens with the live host
 *  ctx and the target's id (module id / edge id / file path / process key / tab
 *  id); it returns the rows to append (empty → contributes nothing). `when(ctx)`
 *  is an outer gate on the whole contribution. */
export interface ContextMenuContribution {
  readonly id: string;
  readonly target: ContextMenuTarget;
  when?(ctx: WorkbenchHostContext): boolean;
  items(ctx: WorkbenchHostContext, targetId: string): readonly MenuItemSpec[];
}

/** The scope a settings page belongs to — the three tab groups in Studio's
 *  SettingsModal (account-wide, per-workspace, per-repo). A contributed page is
 *  injected into its scope group after the built-in tabs. */
export type SettingsScope = "account" | "workspace" | "repo";

/** A settings page contributed into the SettingsModal's scoped tab list. The
 *  telemetry trace-source config is the first real one (repo scope). `when(ctx)`
 *  gates the tab (e.g. repo pages hide when no repo is active); `order` sorts
 *  within the scope group. `Page` renders in the modal's right pane and gets the
 *  live host ctx (its `activeRepo` carries the slug/repo it acts on). Read OUT of
 *  the plugin host (the modal mounts above the editor route) via the bridge. */
export interface SettingsPageContribution {
  readonly id: string;
  readonly scope: SettingsScope;
  readonly title: string;
  readonly icon?: ReactNode;
  readonly order?: number;
  when?(ctx: WorkbenchHostContext): boolean;
  readonly Page: ComponentType<{ readonly ctx: WorkbenchHostContext }>;
}

export interface PluginContributions {
  readonly boardLayers?: readonly BoardLayerContribution[];
  readonly detailSections?: readonly DetailSectionContribution[];
  readonly headerItems?: readonly HeaderItemContribution[];
  readonly statusItems?: readonly StatusItemContribution[];
  readonly navSections?: readonly NavSectionContribution[];
  readonly panelKinds?: readonly PanelKind[];
  readonly commands?: readonly CommandContribution[];
  readonly menuItems?: readonly MenuItemContribution[];
  readonly contextMenu?: readonly ContextMenuContribution[];
  readonly settingsPages?: readonly SettingsPageContribution[];
}

/** The contribution-API version the host implements. A plugin declares the
 *  version it was built against; the host skips plugins built against a
 *  different major version (so an outdated integration degrades to "absent"
 *  rather than crashing against a changed contract). Bump on breaking changes
 *  to the contribution shapes below. */
export const WORKBENCH_PLUGIN_API_VERSION = 1;

export interface WorkbenchPlugin {
  /** Stable identity — also the error-boundary key, so one plugin's crash is
   *  isolated to its own contributions. */
  readonly id: string;
  readonly title: string;
  /** The {@link WORKBENCH_PLUGIN_API_VERSION} this plugin targets. Omit only for
   *  in-tree first-party plugins pinned to the current version; external plugins
   *  should always set it so the host can reject an incompatible build. */
  readonly apiVersion?: number;
  /** Context providers this plugin needs (its data feed). Each is an
   *  instance-bound component a plugin factory built — it closes over config and
   *  internally constructs its store + runs its SSE/EventSource hook (guarded for
   *  headless/SSR). Folded around the whole workbench subtree, so v1 providers may
   *  depend only on context OUTSIDE the ViewerProviders chain. */
  readonly providers?: readonly ComponentType<{ children: ReactNode }>[];
  readonly contributes?: PluginContributions;
}
